/**
 * 二维码渲染组件（前端生成，无服务端负载）。
 * 内容 = PromotionCode 的 qr_code_url（含 /pricing?promo=XXX）。
 * 用 react-qr-code 渲染 SVG，下载时通过 canvas 转 PNG。
 */
import { Button, Card, CopyButton, Group, Stack, Text } from '@mantine/core';
import { IconCopy, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

interface Props {
  code: string;
  url: string;
  size?: number;
}

const svgToPngDataUrl = async (svg: SVGSVGElement, size: number): Promise<string> => {
  const xml = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(svgBlob);
  return await new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Cannot get 2d context'));
        return;
      }
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(objectUrl);
      reject(e);
    };
    img.src = objectUrl;
  });
};

export const QRCodeCard = ({ code, url, size = 240 }: Props) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const getSvg = (): SVGSVGElement | null => containerRef.current?.querySelector('svg') || null;

  const downloadPng = async () => {
    const svg = getSvg();
    if (!svg) return;
    const dataUrl = await svgToPngDataUrl(svg, 512);
    const a = document.createElement('a');
    a.download = `coupon-${code}.png`;
    a.href = dataUrl;
    a.click();
  };

  const printPage = async () => {
    const svg = getSvg();
    if (!svg) return;
    const dataUrl = await svgToPngDataUrl(svg, 512);
    const w = window.open('', '_blank', 'width=600,height=800');
    if (!w) return;
    w.document.write(
      `<html><head><title>${code}</title></head>` +
        `<body style="text-align:center;font-family:sans-serif">` +
        `<h2>${code}</h2><img src="${dataUrl}" style="max-width:80%"/>` +
        `<p style="word-break:break-all">${url}</p></body></html>`
    );
    w.document.close();
    setTimeout(() => w.print(), 200);
  };

  return (
    <Card withBorder p="md" radius="md">
      <Stack align="center" gap="xs">
        <div ref={containerRef} style={{ background: '#fff', padding: 12, width: size, height: size }}>
          <QRCode value={url} size={size - 24} level="M" style={{ height: 'auto', maxWidth: '100%', width: '100%' }} />
        </div>
        <Text fw={600} ff="monospace">
          {code}
        </Text>
        <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all', textAlign: 'center' }}>
          {url}
        </Text>
        <Group gap="xs" mt="xs">
          <CopyButton value={code}>
            {({ copied, copy }) => (
              <Button size="xs" variant="light" leftSection={<IconCopy size={14} />} onClick={copy}>
                {copied ? '✓' : t('coupon.copy_code')}
              </Button>
            )}
          </CopyButton>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={downloadPng}>
            {t('coupon.download_qr')}
          </Button>
          <Button size="xs" variant="light" leftSection={<IconPrinter size={14} />} onClick={printPage}>
            {t('coupon.print')}
          </Button>
          <CopyButton value={url}>
            {({ copied, copy }) => (
              <Button size="xs" variant="light" onClick={copy}>
                {copied ? '✓' : t('coupon.copy_link')}
              </Button>
            )}
          </CopyButton>
        </Group>
      </Stack>
    </Card>
  );
};
